// src/hooks/useAuditFirestore.js
import { useCallback, useRef } from 'react';
import { useFirestore } from './useFirestore';
import { useAuth } from './useAuth';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

// Cache para evitar registrar la misma operación múltiples veces
const recentOperations = new Map();

export const useAuditFirestore = (collectionName) => {
  const firestore = useFirestore(collectionName);
  const { userProfile } = useAuth();
  
  // Usamos useRef para mantener referencia estable a la colección de actividades
  const activityCollection = useRef(collection(db, 'activities')).current;

  // Función para generar descripción automática
  const generateDescription = (collection, operation, data, previousData) => {
    const entityName = data?.name || data?.title || data?.invoiceNumber || data?.companyName || 
                      `${data?.firstName || ''} ${data?.lastName || ''}`.trim() || 'Sin nombre';
    
    const collectionLabels = {
      proposals: 'propuesta',
      contracts: 'contrato',
      projects: 'proyecto',
      clients: 'cliente',
      invoices: 'factura',
      tasks: 'tarea',
      contacts: 'contacto',
    };

    const entityLabel = collectionLabels[collection] || collection;
    
    if (operation === 'add') {
      return `Creó ${entityLabel}: "${entityName}"`;
    }
    
    if (operation === 'update' && previousData) {
      const changes = [];
      
      if (data.status && previousData.status && data.status !== previousData.status) {
        changes.push(`Estado: ${previousData.status} → ${data.status}`);
      }
      
      if (data.version && previousData.version && data.version !== previousData.version) {
        changes.push(`Versión: ${previousData.version} → ${data.version}`);
      }
      
      if (data.total !== undefined && previousData.total !== undefined && data.total !== previousData.total) {
        changes.push(`Importe: ${previousData.total}€ → ${data.total}€`);
      }

      if (changes.length > 0) {
        return `Actualizó ${entityLabel} "${entityName}": ${changes.join(', ')}`;
      }
      return `Actualizó ${entityLabel}: "${entityName}"`;
    }
    
    if (operation === 'remove') {
      return `Eliminó ${entityLabel}: "${entityName}"`;
    }
    
    return `${operation}: "${entityName}"`;
  };

  // Registrar actividad (con debounce para evitar duplicados)
  const logActivity = useCallback(async (operation, data, previousData = null) => {
    try {
      const operationKey = `${collectionName}_${operation}_${data?.id || 'new'}_${Date.now()}`;
      
      // Evitar duplicados en menos de 1 segundo
      if (recentOperations.has(operationKey)) {
        return;
      }
      
      recentOperations.set(operationKey, true);
      setTimeout(() => recentOperations.delete(operationKey), 1000);

      const activityType = `${collectionName}_${operation}`;
      const description = generateDescription(collectionName, operation, data, previousData);
      
      const entityName = data?.name || data?.title || data?.invoiceNumber || 
                        data?.companyName || `${data?.firstName || ''} ${data?.lastName || ''}`.trim() || 
                        previousData?.name || previousData?.title || 'Sin nombre';

      await addDoc(activityCollection, {
        type: activityType,
        description,
        entityId: data?.id || previousData?.id || '',
        entityType: collectionName,
        entityName,
        userId: userProfile?.uid || 'system',
        userName: userProfile?.displayName || userProfile?.email || 'Sistema',
        userEmail: userProfile?.email || '',
        timestamp: serverTimestamp(),
        metadata: {
          operation,
          changes: data,
          previousData: previousData,
        },
        isActive: true,
      });
    } catch (error) {
      // Solo loguear el error, no interrumpir la operación principal
      console.warn('Error logging activity:', error);
    }
  }, [collectionName, userProfile, activityCollection]);

  // Wrapper para add
  const add = useCallback(async (data) => {
    try {
      const result = await firestore.add(data);
      // No esperamos a que se registre la actividad para no ralentizar
      logActivity('add', { ...data, id: result?.id || result });
      return result;
    } catch (error) {
      throw error;
    }
  }, [firestore, logActivity]);

  // Wrapper para update
  const update = useCallback(async (docId, data) => {
    try {
      // Intentar obtener datos anteriores (sin bloquear si falla)
      let previousData = null;
      try {
        const { getDoc, doc } = await import('firebase/firestore');
        const docRef = doc(db, collectionName, docId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          previousData = { id: docSnap.id, ...docSnap.data() };
        }
      } catch (e) {
        // Silencioso - no podemos obtener datos anteriores
      }

      const result = await firestore.update(docId, data);
      logActivity('update', { ...data, id: docId }, previousData);
      return result;
    } catch (error) {
      throw error;
    }
  }, [firestore, logActivity, collectionName]);

  // Wrapper para remove (hard delete)
  // Wrapper para remove (hard delete)
const remove = useCallback(async (docId) => {
  try {
    // VERIFICAR que docId existe
    if (!docId) {
      console.error('Remove called without valid docId');
      return null;
    }

    // Intentar obtener datos antes de eliminar
    let entityData = null;
    try {
      const { getDoc, doc } = await import('firebase/firestore');
      const docRef = doc(db, collectionName, docId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        entityData = { id: docSnap.id, ...docSnap.data() };
      }
    } catch (e) {
      console.warn('Could not fetch document before deletion:', e);
    }

    const result = await firestore.remove(docId);
    if (entityData) {
      logActivity('remove', entityData);
    }
    return result;
  } catch (error) {
    throw error;
  }
}, [firestore, logActivity, collectionName]);

  // Wrapper para softDelete
  const softDelete = useCallback(async (docId) => {
    try {
      const result = await firestore.softDelete(docId);
      logActivity('update', { id: docId, isActive: false }, { id: docId, isActive: true });
      return result;
    } catch (error) {
      throw error;
    }
  }, [firestore, logActivity]);

  // Wrapper para restore
  const restore = useCallback(async (docId) => {
    try {
      const result = await firestore.restore(docId);
      logActivity('update', { id: docId, isActive: true }, { id: docId, isActive: false });
      return result;
    } catch (error) {
      throw error;
    }
  }, [firestore, logActivity]);

  // Los demás métodos no necesitan auditoría o la heredan
  return {
    ...firestore,
    add,
    update,
    remove,
    softDelete,
    restore,
  };
};

export default useAuditFirestore;